import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Globe,
    Settings,
    Plus,
    Trash2,
    RefreshCw,
    ExternalLink,
    AlertCircle,
    ChevronRight,
    Database,
    ShieldCheck,
    ArrowLeft,
    Search,
    Edit2,
    Key
} from 'lucide-react';
import { LysepAPI } from '../../../contexts/ApiContext';
import { toast } from 'react-toastify';

const DNSTab = ({ serverId }) => {
    const navigate = useNavigate();
    const [configs, setConfigs] = useState([]);
    const [zones, setZones] = useState([]);
    const [credentials, setCredentials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isConfigured, setIsConfigured] = useState(false);

    // Config form state
    const [apiUrl, setApiUrl] = useState('');
    const [selectedCredentialId, setSelectedCredentialId] = useState('');
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // Zone management state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedZone, setSelectedZone] = useState(null);
    const [isAddingZone, setIsAddingZone] = useState(false);
    const [newZoneName, setNewZoneName] = useState('');

    // Record management state
    const [records, setRecords] = useState([]);
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [isAddingRecord, setIsAddingRecord] = useState(false);

    const fetchInitialData = async () => {
        setLoading(true);
        setError(null);
        try {
            // 1. Fetch credentials for the dropdown
            const credResp = await LysepAPI.get('/api/credentials');
            if (credResp.data.state) {
                setCredentials(credResp.data.data);
            }

            // 2. Fetch configurations
            const configResp = await LysepAPI.get(`/api/servers/${serverId}/configurations`);
            if (configResp.data.state) {
                const configList = configResp.data.data;
                setConfigs(configList);

                const urlConfig = configList.find(c => c.key === 'TECHNITIUM_API_URL');
                const credConfig = configList.find(c => c.key === 'TECHNITIUM_CREDENTIAL_ID');

                if (urlConfig && urlConfig.value && credConfig && credConfig.value) {
                    setIsConfigured(true);
                    setApiUrl(urlConfig.value);
                    setSelectedCredentialId(credConfig.value);

                    // 3. If configured, fetch zones
                    await fetchZones();
                } else {
                    setIsConfigured(false);
                    // Pre-fill URL if IP is known
                    if (urlConfig) setApiUrl(urlConfig.value);
                    if (credConfig) setSelectedCredentialId(credConfig.value);
                }
            }
        } catch (err) {
            console.error(err);
            setError("Veriler alınırken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const fetchZones = async () => {
        try {
            const zonesResp = await LysepAPI.get(`/api/servers/${serverId}/dns/zones`);
            if (zonesResp.data.state) {
                const zData = zonesResp.data.data;
                // Technitium returns { zones: [], ... } or sometimes raw array
                setZones(Array.isArray(zData) ? zData : (zData?.zones || []));
            }
        } catch (err) {
            console.error(err);
            toast.error("Zone listesi alınamadı: " + (err.response?.data?.message || err.message));
        }
    };

    const fetchRecords = async (zoneName) => {
        setLoadingRecords(true);
        try {
            const resp = await LysepAPI.get(`/api/servers/${serverId}/dns/zones/${zoneName}/records`);
            if (resp.data.state) {
                const rData = resp.data.data;
                // Technitium returns { records: [], ... } for zone records
                setRecords(Array.isArray(rData) ? rData : (rData?.records || []));
            }
        } catch (err) {
            console.error(err);
            toast.error("Kayıtlar alınamadı: " + (err.response?.data?.message || err.message));
        } finally {
            setLoadingRecords(false);
        }
    };

    useEffect(() => {
        if (serverId) {
            fetchInitialData();
        }
    }, [serverId]);

    // useEffect(() => {
    //     if (selectedZone) {
    //         fetchRecords(selectedZone);
    //     } else {
    //         setRecords([]);
    //     }
    // }, [selectedZone]);

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        if (!selectedCredentialId) {
            toast.error("Lütfen bir API Key seçin.");
            return;
        }

        setIsSavingConfig(true);
        try {
            // Save URL
            await LysepAPI.post(`/api/servers/${serverId}/configurations`, {
                key: 'TECHNITIUM_API_URL',
                value: apiUrl,
                description: 'Technitium DNS API Endpoint URL'
            });

            // Save Credential ID
            await LysepAPI.post(`/api/servers/${serverId}/configurations`, {
                key: 'TECHNITIUM_CREDENTIAL_ID',
                value: selectedCredentialId,
                description: 'Selected API Key (Credential ID)'
            });

            toast.success("Yapılandırma kaydedildi.");
            setIsConfigured(true);
            fetchZones();
        } catch (err) {
            toast.error("Kaydedilemedi: " + (err.response?.data?.message || err.message));
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleAddZone = async (e) => {
        e.preventDefault();
        if (!newZoneName) return;

        try {
            const resp = await LysepAPI.post(`/api/servers/${serverId}/dns/zones`, {
                zoneName: newZoneName,
                type: 'primary'
            });
            if (resp.data.state) {
                toast.success("Zone başarıyla eklendi.");
                setNewZoneName('');
                setIsAddingZone(false);
                fetchZones();
            }
        } catch (err) {
            toast.error("Ekleme hatası: " + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteZone = async (zoneName) => {
        if (!window.confirm(`${zoneName} zone'unu silmek istediğinizden emin misiniz?`)) return;

        try {
            const resp = await LysepAPI.delete(`/api/servers/${serverId}/dns/zones/${zoneName}`);
            if (resp.data.state) {
                toast.success("Zone silindi.");
                fetchZones();
            }
        } catch (err) {
            toast.error("Silme hatası: " + (err.response?.data?.message || err.message));
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                <p>Yükleniyor...</p>
            </div>
        );
    }

    if (!isConfigured) {
        return (
            <div className="max-w-2xl mx-auto py-10 animate-fadeIn">
                <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-blue-500/10 rounded-xl">
                            <Settings className="w-8 h-8 text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Technitium Kurulumu</h2>
                            <p className="text-sm text-gray-400">DNS yönetimi için Technitium API ve API Key seçmeniz gerekiyor.</p>
                        </div>
                    </div>

                    <form onSubmit={handleSaveConfig} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Technitium API Endpoint URL</label>
                            <div className="relative">
                                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <input
                                    type="url"
                                    required
                                    value={apiUrl}
                                    onChange={(e) => setApiUrl(e.target.value)}
                                    placeholder="http://1.2.3.4:5380"
                                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-mono"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">API Key (Credential)</label>
                            <div className="relative">
                                <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <select
                                    required
                                    value={selectedCredentialId}
                                    onChange={(e) => setSelectedCredentialId(e.target.value)}
                                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all appearance-none cursor-pointer"
                                >
                                    <option value="" className="bg-[#1a1a1a]">Bir API Key seçin...</option>
                                    {credentials.map(cred => (
                                        <option key={cred.id} value={cred.id} className="bg-[#1a1a1a]">
                                            {cred.name} ({cred.type})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                Not: Technitium API Token'ı, seçilen Kimlik Bilgisinin (Credential) "şifre" alanında saklanmalıdır.
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={isSavingConfig}
                            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                        >
                            {isSavingConfig ? <RefreshCw className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                            Yapılandırmayı Kaydet ve Bağlan
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col gap-6 animate-fadeIn pb-10">
            {/* Header / Stats */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-blue-500/10 rounded-xl border border-blue-500/20">
                        <Globe className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-white">DNS Yönetimi</h2>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-400">{apiUrl}</p>
                            <span className="w-1 h-1 bg-gray-600 rounded-full"></span>
                            <p className="text-xs text-blue-400 flex items-center gap-1">
                                <Key className="w-3 h-3" />
                                {credentials.find(c => String(c.id) === String(selectedCredentialId))?.name || 'API Key'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsAddingZone(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all shadow-lg shadow-blue-500/20"
                    >
                        <Plus className="w-4 h-4" />
                        Yeni Zone Ekle
                    </button>
                    <button
                        onClick={() => setIsConfigured(false)}
                        className="p-2 text-gray-400 hover:text-white bg-[#1a1a1a] hover:bg-[#252525] border border-white/10 rounded-lg transition-all"
                        title="Yapılandırmayı Düzenle"
                    >
                        <Settings className="w-5 h-5" />
                    </button>
                    <button
                        onClick={fetchZones}
                        className="p-2 text-gray-400 hover:text-white bg-[#1a1a1a] hover:bg-[#252525] border border-white/10 rounded-lg transition-all"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Zone Search */}
            <div className="relative group shrink-0">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                <input
                    type="text"
                    placeholder="Zone ara (örn: example.com)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all shadow-lg"
                />
            </div>

            {/* Zone List */}
            <div className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-2xl overflow-hidden flex flex-col shadow-xl">
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="bg-black/20 text-gray-400 font-medium border-b border-white/5 uppercase tracking-wider text-[11px]">
                                <th className="px-6 py-4">Zone Adı / Domain</th>
                                <th className="px-6 py-4">Tür</th>
                                <th className="px-6 py-4">Durum</th>
                                <th className="px-6 py-4 text-right">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {(zones || [])
                                .filter(z => z.name.toLowerCase().includes(searchQuery.toLowerCase()))
                                .map((zone) => (
                                    <tr
                                        key={zone.name}
                                        onClick={() => navigate(`/panel/servers/${serverId}/dns/management/${zone.name}/records`)}
                                        className="group hover:bg-white/[0.02] transition-colors cursor-pointer"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors">
                                                    <Globe className="w-4 h-4 text-blue-400" />
                                                </div>
                                                <span className="text-white font-medium text-base">{zone.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded text-[10px] font-bold border border-purple-500/20 uppercase">
                                                {zone.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-1.5 h-1.5 rounded-full ${zone.disabled ? 'bg-red-500' : 'bg-green-500'}`} />
                                                <span className="text-gray-400 text-xs">{zone.disabled ? 'Devre Dışı' : 'Aktif'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/panel/servers/${serverId}/dns/management/${zone.name}/records`)
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"
                                                    title="Yönet"
                                                >
                                                    <Settings className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteZone(zone.name);
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                                    title="Sil"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                            {(!zones || zones.length === 0) && !loading && (
                                <tr>
                                    <td colSpan="4" className="py-20 text-center text-gray-500 italic">
                                        <div className="flex flex-col items-center justify-center">
                                            <Globe className="w-12 h-12 mb-3 opacity-10" />
                                            <p>Henüz hiç Zone oluşturulmamış.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Zone Modal */}
            {isAddingZone && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 shadow-2xl w-full max-w-md animate-scaleUp">
                        <h3 className="text-xl font-bold text-white mb-4">Yeni Zone Ekle</h3>
                        <form onSubmit={handleAddZone} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">Zone Adı (Domain)</label>
                                <input
                                    type="text"
                                    autoFocus
                                    required
                                    placeholder="example.com"
                                    value={newZoneName}
                                    onChange={(e) => setNewZoneName(e.target.value)}
                                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsAddingZone(false)}
                                    className="flex-1 px-4 py-2.5 bg-[#252525] hover:bg-[#303030] text-gray-300 font-medium rounded-xl transition-all"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20"
                                >
                                    Ekle
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DNSTab;
